import React from "react"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgEdit03 } from "@digits-shared/components/SVGIcons/line/Edit03.svg"
import { SvgZapFast } from "@digits-shared/components/SVGIcons/line/ZapFast.svg"
import { DigitsButton } from "@digits-shared/DesignSystem/Button"
import { Snackbar } from "@digits-shared/DesignSystem/Snackbar"
import zIndexes from "@digits-shared/DesignSystem/zIndexes"
import stringHelper from "@digits-shared/helpers/stringHelper"
import colors from "@digits-shared/themes/colors"
import { H2Text } from "@digits-shared/themes/typography"
import { m, type Variants } from "framer-motion"
import styled from "styled-components"
import { type TransactionUpdate } from "src/frontend/components/OS/Applications/Accounting/TransactionDashboard/state/TransactionsByCategory/types"
import { BULK_HEADER_HEIGHT } from "src/frontend/components/OS/Applications/Shared/Accounting/BulkActions/types"

/*
  STYLES
*/

const BULK_STATUS_UPDATE_LIMIT = 200

const EditIcon = styled(SvgEdit03)`
  width: 24px;
  height: 24px;
  ${svgPathStyles(colors.secondary, 1.5)};
`

const Header = styled(m.div)`
  position: absolute;
  top: 0;
  z-index: ${zIndexes.stickHeaderContent};

  width: 100%;
  height: ${BULK_HEADER_HEIGHT}px;
  min-height: ${BULK_HEADER_HEIGHT}px;

  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  .digits-mac & {
    padding-top: calc(16px + 22px);
    height: calc(${BULK_HEADER_HEIGHT}px + 22px);
  }
`

const HeaderBackground = styled(m.div)`
  position: fixed;
  z-index: ${zIndexes.stickHeaderContent};
  top: 0;
  left: 0;
  right: 0;

  width: 100vw;
  height: ${BULK_HEADER_HEIGHT}px;
  min-height: ${BULK_HEADER_HEIGHT}px;

  background: ${colors.secondary05};
  backdrop-filter: blur(100px);
  box-shadow: 0 0 20px 0 rgba(16, 25, 63, 0.3);
  border-bottom: 1px solid ${colors.translucentWhite60};

  .digits-mac & {
    padding-top: calc(16px + 22px);
    height: calc(${BULK_HEADER_HEIGHT}px + 22px);
  }
`

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`

/*
  COMPONENTS
*/

const variants: Variants = {
  visible: { y: 0 },
  hidden: { y: "-100%" },
}

interface TransactionUpdatesHeaderProps {
  updates: Map<string, Partial<TransactionUpdate>>
  disabled?: boolean
  onCancel: () => void
  onUpdate: () => void
  className?: string
}

export const TransactionUpdatesHeader: React.FC<TransactionUpdatesHeaderProps> = ({
  updates,
  onCancel,
  className,
  onUpdate,
  disabled = false,
}) => (
  <>
    <HeaderBackground
      variants={variants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      transition={{ duration: 0.2 }}
    />

    <Header
      variants={variants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      transition={{ duration: 0.2 }}
      className={className}
    >
      <EditIcon />
      <H2Text weight="normal">
        Editing {updates.size}{" "}
        {stringHelper.pluralize(updates.size, "transaction…", "transactions…")}
      </H2Text>

      <ButtonContainer>
        <DigitsButton onClick={onCancel} $variant="secondary-dark" disabled={disabled}>
          Cancel
        </DigitsButton>

        <DigitsButton
          disabled={disabled || updates.size > BULK_STATUS_UPDATE_LIMIT}
          onClick={onUpdate}
        >
          <SvgZapFast />
          Apply {updates.size}
        </DigitsButton>
      </ButtonContainer>
    </Header>

    {updates.size >= BULK_STATUS_UPDATE_LIMIT && (
      <Snackbar
        message="Bulk actions limit reached"
        description={`You can only edit up to ${BULK_STATUS_UPDATE_LIMIT} transactions at a time. Please complete this batch and repeat the action for the remaining transactions.`}
      />
    )}
  </>
)
